import { NumberField } from "@base-ui/react/number-field";
import minusIcon from "@loot-os/icons/icons/minus";
import plusIcon from "@loot-os/icons/icons/plus";
import { useId } from "react";

import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { Icon } from "./Icon.js";
import { iconButtonStyles } from "./IconButton.styles.js";

export interface StepperProps {
  /** What the number counts: "Rounds of Tide Pool". Names the group and the field. */
  label: string;
  /** Shows the label above the stepper; by default it is the group's name alone. */
  showLabel?: boolean;
  value: number;
  /** Called with the next value, already held within `min` and `max`. */
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  /** How far − and + (and the arrow keys) move it. */
  step?: number;
  /** The − button's name: "One fewer round of Tide Pool". */
  decrementLabel?: string;
  /** The + button's name: "One more round of Tide Pool". */
  incrementLabel?: string;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
}

const VALUE_WIDTH = { sm: "w-8", md: "w-10" } as const;

/**
 * A whole number nudged with − and +: rounds of a box, seats. The number is a spin button, so
 * the arrow keys step it, Page Up and Down take ten steps, Home and End jump to the limits, and
 * a typed number is held within them. − and + stop at the limits and say so by graying out.
 */
export function Stepper({
  label,
  showLabel = false,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  decrementLabel = "Decrease",
  incrementLabel = "Increase",
  size = "sm",
  disabled,
  className,
}: StepperProps) {
  const labelId = useId();
  return (
    <NumberField.Root
      value={value}
      onValueChange={(next) => {
        if (next !== null) onValueChange(next);
      }}
      min={min}
      max={max}
      step={step}
      largeStep={step * 10}
      disabled={disabled}
      className={cn("flex min-w-0 flex-col gap-1.5", className)}
    >
      {showLabel && (
        <span id={labelId} className="font-body text-body-b2-strong text-text-secondary">
          {label}
        </span>
      )}
      <NumberField.Group
        aria-label={showLabel ? undefined : label}
        aria-labelledby={showLabel ? labelId : undefined}
        className="flex min-w-0 items-center justify-between gap-1"
      >
        <NumberField.Decrement aria-label={decrementLabel} className={iconButtonStyles({ size })}>
          <Icon icon={minusIcon} size={size === "sm" ? 16 : 20} />
        </NumberField.Decrement>
        <NumberField.Input
          aria-label={label}
          className={cn(
            "min-w-0 rounded-sm bg-transparent text-center font-mono text-mono-price text-text-primary tabular-nums data-disabled:text-text-disabled",
            FOCUS_RING,
            VALUE_WIDTH[size],
          )}
        />
        <NumberField.Increment aria-label={incrementLabel} className={iconButtonStyles({ size })}>
          <Icon icon={plusIcon} size={size === "sm" ? 16 : 20} />
        </NumberField.Increment>
      </NumberField.Group>
    </NumberField.Root>
  );
}
